import './style.scss';
import { TabsProps } from 'antd';
import { useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useQuery } from '@tanstack/react-query';
import { Filter } from '../../../../@types/filter';
import { LOCATION, LOCATION_GROUPS } from '../../../../constants/QueryKey';
import { locationApi } from '../../../../apis';
import Widget from '../../../../components/Widget';
import TableWrap from '../../../../components/TableWrap';
import { User, TimekeepingReportTypeEnum } from '../../../../apis/client-axios';
import { ColumnsType } from 'antd/lib/table';

enum LocationTabEnum {
  CURRENT = 'current',
  ABSENCE = 'absence',
}
const defaultOtherColumnWidth = 252;
const fixedLeftColumnWidth = 290;
const Location = () => {
  const intl = useIntl();
  const [tab, setTab] = useState<LocationTabEnum>(LocationTabEnum.CURRENT);
  const [filter, setFilter] = useState<Filter>({ page: 1, pageSize: 20, fullTextSearch: '' });
  const tableWrapperRef = useRef<HTMLDivElement>(null);

  const { data: dataUserLocation, isLoading } = useQuery({
    queryKey: [LOCATION, tab, filter],
    queryFn: () => {
      if (tab === LocationTabEnum.CURRENT) {
        return locationApi.locationControllerGetUsersLocation(
          filter.page,
          filter.pageSize,
          undefined,
          filter.fullTextSearch,
          undefined,
        );
      } else {
        return locationApi.locationControllerGetUsersAbsenceNow(
          filter.page,
          filter.pageSize,
          undefined,
          filter.fullTextSearch,
          undefined,
        );
      }
    },
  });

  const { data: dataLocation } = useQuery({
    queryKey: [LOCATION_GROUPS, tab],
    queryFn: () => {
      if (tab === LocationTabEnum.CURRENT) {
        return locationApi.locationControllerGetAll(1, undefined, undefined, undefined, undefined);
      }
    },
  });
  const tableWidth = Number(tableWrapperRef.current?.clientWidth);
  const otherColumnsWidth =
    Number(tableWidth - fixedLeftColumnWidth) / Number(dataLocation?.data?.data?.content?.length || 1);

  const columns: ColumnsType = [
    {
      title: intl.formatMessage({ id: 'location.employee.name' }),
      dataIndex: 'name',
      key: 'name',
      width: fixedLeftColumnWidth,
      fixed: 'left',
      render: (_, record) => {
        return <p className="location_management-content-username">{record?.name}</p>;
      },
    },
    ...(tab === LocationTabEnum.CURRENT && Array.isArray(dataLocation?.data?.data?.content)
      ? dataLocation?.data?.data?.content?.map((item) => ({
          title: item?.name,
          dataIndex: item?.id,
          key: item?.id,
          width: otherColumnsWidth < defaultOtherColumnWidth ? defaultOtherColumnWidth : otherColumnsWidth,
          render: (_, record) => {
            const hasMatchingLocation = (record as User)?.locationId === item?.id;
            return hasMatchingLocation ? (
              <div className="location-data">
                <div className="location-data_content"></div>
              </div>
            ) : null;
          },
        }))
      : [
          {
            title: intl.formatMessage({ id: 'location.bussinessTrip' }),
            dataIndex: 'businessTrip',
            key: 'businessTrip',
            width: otherColumnsWidth < defaultOtherColumnWidth ? defaultOtherColumnWidth : otherColumnsWidth,
            render: (_, record) => {
              return record?.timekeepingReports?.[0]?.type === TimekeepingReportTypeEnum.RequestBusiness ? (
                <div className="location-data">
                  <div className="location-data_content"></div>
                </div>
              ) : null;
            },
          },
          {
            title: intl.formatMessage({ id: 'location.outside' }),
            dataIndex: 'outside',
            key: 'outside',
            width: otherColumnsWidth < defaultOtherColumnWidth ? defaultOtherColumnWidth : otherColumnsWidth,
            render: (_, record) => {
              return record?.timekeepingReports?.[0]?.type === TimekeepingReportTypeEnum.OutsidePrivate ||
                record?.timekeepingReports?.[0]?.type === TimekeepingReportTypeEnum.OutsideBusiness ? (
                <div className="location-data">
                  <div className="location-data_content"></div>
                </div>
              ) : null;
            },
          },
          {
            title: intl.formatMessage({ id: 'location.paidVacation' }),
            dataIndex: 'paidVacation',
            key: 'paidVacation',
            width: otherColumnsWidth < defaultOtherColumnWidth ? defaultOtherColumnWidth : otherColumnsWidth,
            render: (_, record) => {
              return record?.timekeepingReports?.[0]?.type === TimekeepingReportTypeEnum.RequestDayOff ? (
                <div className="location-data">
                  <div className="location-data_content"></div>
                </div>
              ) : null;
            },
          },
        ]),
  ];

  const items: TabsProps['items'] = [
    {
      key: LocationTabEnum.CURRENT,
      label: intl.formatMessage({ id: 'location.present' }),
    },
    {
      key: LocationTabEnum.ABSENCE,
      label: intl.formatMessage({ id: 'location.absent' }),
    },
  ];

  return (
    <div className="location_management">
      <div className="location_management-content">
        <Widget
          breadcrumbs={[
            intl.formatMessage({ id: 'adminMenu.locationManagement' }),
            intl.formatMessage({ id: 'adminMenu.location' }),
          ]}
          title={intl.formatMessage({ id: 'adminMenu.location' })}
          tabProps={{
            items,
            onChange: (key: string) => {
              setTab(key as LocationTabEnum);
            },
          }}
          content={
            <TableWrap
              isPagination
              loading={isLoading}
              tableWrapperRef={tableWrapperRef}
              onSearch={(search) => {
                setFilter((prevFilters) => ({
                  ...prevFilters,
                  page: 1,
                  fullTextSearch: search,
                }));
              }}
              inputProps={{
                placeholder: intl.formatMessage({ id: 'location.searchBox' }),
              }}
              tableProps={{
                dataSource: Array.isArray(dataUserLocation?.data?.data?.content)
                  ? dataUserLocation?.data?.data?.content
                  : [],
                columns,
                scroll: {
                  x: Number(columns?.[1]?.width || 0) === defaultOtherColumnWidth ? 1000 : true,
                },
              }}
              paginationProps={{
                disabled: false,
                current: dataUserLocation?.data?.data?.page || 1,
                pageSize: dataUserLocation?.data?.data?.size || 20,
                total: dataUserLocation?.data?.data?.total || 0,
                onChange: (page, pageSize) =>
                  setFilter((prevFilters) => ({
                    ...prevFilters,
                    page,
                    pageSize,
                  })),
              }}
            />
          }
        />
      </div>
    </div>
  );
};

export default Location;
